console.log('[GrapheneSection] Script starting to load');

class GrapheneSectionComponent extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.resultsContainer = null;
    this.sliderElement = null;
    this.sliderComponent = null;
    this.controlsElement = null;
    this.dotsElement = null;
    this.loadingElement = null;
    this.mode = 'product_recommendations';
    this.elementId = 'recommended';
    this.placement = '';
    this.maxProducts = null;
    this.sectionId = null;
    this.sliderId = null;
    this.productId = null;
    this.showDots = false;
    this.showArrows = true;
    this.grapheneReadyAttempts = 0;
  }

  connectedCallback() {
    this.removeAttribute('hidden');
    this.style.display = '';
    this.grapheneReadyAttempts = 0;
    this.captureAttributes();

    if (!this.validateEnvironment()) {
      return;
    }

    this.initializeIntersectionObserver();
  }

  captureAttributes() {
    this.sectionId = this.getAttribute('section-id') || `graphene-${Date.now()}`;
    this.sliderId = this.getAttribute('slider-id');
    this.mode = this.getAttribute('data-mode') || 'product_recommendations';
    this.elementId = this.getAttribute('data-element-id') || 'recommended';
    this.placement = this.getAttribute('data-placement') || '';

    const maxProductsAttr = parseInt(this.getAttribute('max-products'), 10);
    this.maxProducts = Number.isNaN(maxProductsAttr) ? null : maxProductsAttr;

    this.productId = this.getAttribute('product-id');

    this.controlsElement = this.getTargetElement(this.getAttribute('controls-target'));
    this.dotsElement = this.getTargetElement(this.getAttribute('dots-target'));
    this.loadingElement = this.getTargetElement(this.getAttribute('loading-target'));

    this.sliderElement = this.sliderId ? document.getElementById(this.sliderId) : null;
    this.sliderComponent = this.sliderElement ? this.sliderElement.closest('slider-component') : null;

    const showArrowsAttr = this.getAttribute('data-show-arrows');
    this.showArrows = showArrowsAttr === null || showArrowsAttr === 'true';
    this.showDots = this.getAttribute('data-show-dots') === 'true';
  }

  getTargetElement(id) {
    if (!id) return null;
    return document.getElementById(id);
  }

  validateEnvironment() {
    if (!this.sliderElement) {
      console.error('[GrapheneSection] Missing slider element', this.sliderId);
      this.showError('Slider container missing');
      return false;
    }

    if (this.mode === 'product_recommendations' && !this.validateProductId()) {
      return false;
    }

    return true;
  }

  validateProductId() {
    if (!this.productId || this.productId === 'null' || this.productId === 'undefined') {
      console.error('[GrapheneSection] Invalid product ID provided:', this.productId);
      this.showError('Product ID is required');
      return false;
    }

    const productIdNum = parseInt(this.productId, 10);
    if (Number.isNaN(productIdNum) || productIdNum <= 0) {
      console.error('[GrapheneSection] Product ID must be a valid number:', this.productId);
      this.showError('Invalid product ID');
      return false;
    }

    this.productId = productIdNum.toString();
    return true;
  }

  initializeIntersectionObserver() {
    this.observer?.disconnect();
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0] || !entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadContent();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  async loadContent() {
    try {
      await this.ensureGrapheneReady();
    } catch (error) {
      console.error('[GrapheneSection] Graphene not available', error);
      this.showError('GrapheneHC not available');
      return;
    }

    if (!this.resultsContainer) {
      this.resultsContainer = document.createElement('div');
      this.resultsContainer.className = 'graphene-results';
      this.resultsContainer.id = `graphene-section-${Date.now()}`;
      this.resultsContainer.style.display = 'none';
      this.appendChild(this.resultsContainer);
    }

    requestAnimationFrame(() => {
      const targetSelector = `#${this.resultsContainer.id}`;
      const targetElement = document.querySelector(targetSelector);

      if (!targetElement) {
        console.error('[GrapheneSection] Target element not found:', targetSelector);
        this.showError('Target element not found');
        return;
      }

      const pageContext = this.buildPageContext();
      const args = this.buildArgs();

      window.$g.load({
        elements: [{ id: this.elementId, target: targetSelector }],
        page_context: pageContext,
        args,
        success: () => this.onContentLoaded(),
        error: (err) => {
          console.error('[GrapheneSection] Load failed:', err);
          this.showError(`Failed to load content: ${err?.message || err}`);
        },
      });
    });
  }

  ensureGrapheneReady(maxAttempts = 20, delayMs = 200) {
    if (window.$g) {
      return Promise.resolve();
    }

    if (this.grapheneReadyAttempts >= maxAttempts) {
      return Promise.reject(new Error('GrapheneHC global not found'));
    }

    this.grapheneReadyAttempts += 1;

    return new Promise((resolve, reject) => {
      setTimeout(() => {
        this.ensureGrapheneReady(maxAttempts, delayMs).then(resolve).catch(reject);
      }, delayMs);
    });
  }

  buildPageContext() {
    const context = { section_id: this.sectionId };

    const grapheneLangId = this.getAttribute('graphene-lang-id');
    const grapheneCurrencyId = this.getAttribute('graphene-currency-id');

    context.language_id = grapheneLangId;
    context.currency_id = grapheneCurrencyId;

    if (this.mode === 'product_recommendations') {
      context.product_id = this.productId;
    }

    if (this.mode === 'trending_products' && this.placement) {
      context.placement = this.placement;
    }

    return context;
  }

  buildArgs() {
    const args = { split_variants: 1 };
    if (this.maxProducts) {
      args.limit = this.maxProducts;
    }
    return args;
  }

  onContentLoaded() {
    if (this.loadingElement) {
      this.loadingElement.setAttribute('hidden', 'true');
    }

    if (!this.resultsContainer) {
      this.showError('No Graphene results container');
      return;
    }

    const items = this.collectItems();
    if (!items.length) {
      console.warn('[GrapheneSection] No products returned');
      this.hideComponent();
      return;
    }

    const fragment = document.createDocumentFragment();
    const limit = this.maxProducts ? Math.min(this.maxProducts, items.length) : items.length;

    for (let index = 0; index < limit; index += 1) {
      const source = items[index];
      const slide = this.createSlideFromElement(source, index);
      fragment.appendChild(slide);
    }

    this.sliderElement.innerHTML = '';
    this.sliderElement.appendChild(fragment);

    if (this.resultsContainer) {
      this.resultsContainer.replaceChildren();
      this.resultsContainer.remove();
      this.resultsContainer = null;
    }

    this.initializeInteractiveElements();
    this.refreshSlider();
    this.classList.add('graphene-section--loaded');

    const wrapper = this.closest('.graphene-section-content');
    if (wrapper) {
      wrapper.classList.add('graphene-section-ready');
    }

    this.setAttribute('hidden', 'true');
  }

  collectItems() {
    if (!this.resultsContainer) return [];

    const explicitItems = Array.from(this.resultsContainer.querySelectorAll('[data-graphene-product]'));
    if (explicitItems.length) {
      return explicitItems;
    }

    const list = this.resultsContainer.querySelector('ul, ol');
    if (list) {
      return Array.from(list.children).filter((el) => el.nodeType === Node.ELEMENT_NODE);
    }

    return Array.from(this.resultsContainer.children).filter(
      (el) => el.nodeType === Node.ELEMENT_NODE && el.tagName !== 'SCRIPT' && el.tagName !== 'STYLE'
    );
  }

  createSlideFromElement(element, index) {
    let slideElement = element;

    if (slideElement.tagName && slideElement.tagName.toLowerCase() === 'template') {
      const templateContent = slideElement.content ? slideElement.content.cloneNode(true) : null;
      slideElement = document.createElement('div');
      if (templateContent) {
        slideElement.appendChild(templateContent);
      }
    }

    if (!(slideElement.tagName && slideElement.tagName.toLowerCase() === 'li')) {
      const wrapper = document.createElement('li');
      wrapper.classList.add('grid__item', 'slider__slide');
      wrapper.appendChild(slideElement);
      slideElement = wrapper;
    } else {
      slideElement.classList.add('grid__item', 'slider__slide');
    }

    slideElement.id = `Slide-${this.sectionId}-${index + 1}`;
    return slideElement;
  }

  initializeInteractiveElements() {
    if (!this.sliderElement) return;

    const addToCartButtons = this.sliderElement.querySelectorAll('[data-variant-id]');
    addToCartButtons.forEach((button) => {
      if (button.dataset.grapheneInitialized === 'true') {
        return;
      }

      button.dataset.grapheneInitialized = 'true';
      button.addEventListener('click', (event) => {
        const variantId = button.getAttribute('data-variant-id');
        if (!variantId) return;
        event.preventDefault();
        this.handleAddToCart(variantId, button);
      });
    });
  }

  handleAddToCart(variantId, button) {
    button.classList.add('is-loading');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
          },
        ],
      }),
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((error) => {
            throw error;
          });
        }
        return response.json();
      })
      .then((data) => {
        button.classList.remove('is-loading');
        const event = new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart: data },
        });
        document.dispatchEvent(event);
      })
      .catch((error) => {
        console.error('[GrapheneSection] Failed to add to cart', error);
        button.classList.remove('is-loading');
      });
  }

  refreshSlider() {
    if (this.sliderComponent && typeof this.sliderComponent.resetPages === 'function') {
      this.sliderComponent.resetPages();
    }

    const totalSlides = this.sliderElement ? this.sliderElement.querySelectorAll('li').length : 0;
    this.updateControlsVisibility(totalSlides);
  }

  updateControlsVisibility(totalSlides) {
    if (this.controlsElement) {
      const totalElement = this.controlsElement.querySelector('.slider-counter--total');
      if (totalElement) {
        totalElement.textContent = totalSlides.toString();
      }

      if (this.showArrows && totalSlides > 1) {
        this.controlsElement.removeAttribute('hidden');
      } else {
        this.controlsElement.setAttribute('hidden', 'true');
      }
    }

    if (this.dotsElement) {
      if (this.showDots && totalSlides > 1) {
        this.dotsElement.removeAttribute('hidden');
      } else {
        this.dotsElement.setAttribute('hidden', 'true');
      }
    }
  }

  showError(message) {
    if (this.loadingElement) {
      this.loadingElement.removeAttribute('hidden');
      this.loadingElement.innerHTML = `<p class="loading-text">${message}</p>`;
    } else {
      const errorContainer = document.createElement('div');
      errorContainer.className = 'graphene-section-error';
      errorContainer.innerHTML = `<p>${message}</p>`;
      this.appendChild(errorContainer);
    }
  }

  hideComponent() {
    const wrapper = this.closest('.graphene-section-content') || this;
    wrapper.style.display = 'none';
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

if (!customElements.get('graphene-section')) {
  customElements.define('graphene-section', GrapheneSectionComponent);
}

if (!customElements.get('graphene-recommendations')) {
  class GrapheneRecommendationsAlias extends GrapheneSectionComponent {}
  customElements.define('graphene-recommendations', GrapheneRecommendationsAlias);
}

console.log('[GrapheneSection] Script loaded successfully');
